import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { superAdminAPI } from '../../lib/walletApi';
import walletApi from '../../lib/walletApi';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import {
  Building2, Plus, Loader2, Users, MapPin, Upload, Image as ImageIcon,
  Wallet, ArrowUpRight, ChevronRight, Globe, Mail, Phone, UserPlus, Shield, Trash2
} from 'lucide-react';
import { toast } from 'sonner';

const API_BASE = process.env.REACT_APP_BACKEND_URL || '';

function CompanyLogo({ logoUrl, name, size = 'md' }) {
  const [imgSrc, setImgSrc] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (!logoUrl) { setError(true); return; }
    const token = localStorage.getItem('wallet_token');
    const url = `${API_BASE}/api/files/${logoUrl}`;
    fetch(url, { headers: token ? { Authorization: `Bearer ${token}` } : {} })
      .then(r => r.ok ? r.blob() : Promise.reject())
      .then(blob => setImgSrc(URL.createObjectURL(blob)))
      .catch(() => setError(true));
    return () => { if (imgSrc) URL.revokeObjectURL(imgSrc); };
  }, [logoUrl]);

  const dim = size === 'lg' ? 'w-14 h-14' : 'w-10 h-10';
  const textSize = size === 'lg' ? 'text-lg' : 'text-sm';

  if (!error && imgSrc) {
    return <img src={imgSrc} alt={name} className={`${dim} rounded-lg object-cover`} />;
  }
  return (
    <div className={`${dim} rounded-lg bg-pink-50 flex items-center justify-center shrink-0`}>
      <span className={`font-bold text-pink-600 ${textSize}`}>{name?.[0]?.toUpperCase() || 'C'}</span>
    </div>
  );
}

export default function CompanyManagement() {
  const navigate = useNavigate();
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    name: '', slug: '', email: '', phone: '', city: '', state: '',
    registration_number: '', tax_id: '', subscription_tier: 'standard',
  });
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [walletInfo, setWalletInfo] = useState(null);
  const [walletLoading, setWalletLoading] = useState(false);
  const fileInputRef = useRef(null);

  // Admin management state
  const [adminCompany, setAdminCompany] = useState(null);
  const [admins, setAdmins] = useState([]);
  const [adminsLoading, setAdminsLoading] = useState(false);
  const [adminForm, setAdminForm] = useState({ first_name: '', last_name: '', email: '', password: '', phone: '', role: 'admin', permissions: ['*'] });
  const [adminSubmitting, setAdminSubmitting] = useState(false);

  const load = async () => {
    try { setCompanies(await superAdminAPI.getCompanies()); }
    catch { toast.error('Failed to load companies'); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const handleLogoSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { toast.error('File too large (max 5MB)'); return; }
    setLogoFile(file);
    const reader = new FileReader();
    reader.onload = (ev) => setLogoPreview(ev.target.result);
    reader.readAsDataURL(file);
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => { if (v) fd.append(k, v); });
      if (logoFile) fd.append('logo', logoFile);
      await superAdminAPI.createCompanyWithLogo(fd);
      toast.success('Company created successfully');
      setOpen(false);
      setForm({ name: '', slug: '', email: '', phone: '', city: '', state: '', registration_number: '', tax_id: '', subscription_tier: 'standard' });
      setLogoFile(null);
      setLogoPreview(null);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create company');
    } finally { setSubmitting(false); }
  };

  const handleViewWallet = async (company) => {
    setSelectedCompany(company);
    setWalletLoading(true);
    try {
      const info = await superAdminAPI.getCompanyWallet(company.id);
      setWalletInfo(info);
    } catch { toast.error('Failed to load wallet info'); }
    finally { setWalletLoading(false); }
  };

  const handleUploadLogo = async (companyId) => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.onchange = async (e) => {
      const file = e.target.files[0];
      if (!file) return;
      const fd = new FormData();
      fd.append('logo', file);
      try {
        await superAdminAPI.uploadCompanyLogo(companyId, fd);
        toast.success('Logo updated');
        load();
      } catch { toast.error('Upload failed'); }
    };
    input.click();
  };

  const handleViewAdmins = async (company) => {
    setAdminCompany(company);
    setAdminsLoading(true);
    try {
      const list = await superAdminAPI.getCompanyAdmins(company.id);
      setAdmins(Array.isArray(list) ? list : []);
    } catch { toast.error('Failed to load admins'); }
    finally { setAdminsLoading(false); }
  };

  const handleCreateAdmin = async (e) => {
    e.preventDefault();
    setAdminSubmitting(true);
    try {
      await superAdminAPI.createCompanyAdmin(adminCompany.id, {
        first_name: adminForm.first_name,
        last_name: adminForm.last_name,
        email: adminForm.email,
        password: adminForm.password,
        phone: adminForm.phone,
        role: adminForm.role,
      });
      toast.success('Company admin created');
      setAdminForm({ first_name: '', last_name: '', email: '', password: '', phone: '', role: 'admin', permissions: ['*'] });
      const list = await superAdminAPI.getCompanyAdmins(adminCompany.id);
      setAdmins(Array.isArray(list) ? list : []);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create admin');
    } finally { setAdminSubmitting(false); }
  };

  const handleDeleteAdmin = async (admin) => {
    if (!window.confirm(`Remove ${admin.first_name} ${admin.last_name} as admin?`)) return;
    try {
      await superAdminAPI.deleteCompanyAdmin(adminCompany.id, admin.id);
      toast.success('Admin removed');
      const list = await superAdminAPI.getCompanyAdmins(adminCompany.id);
      setAdmins(Array.isArray(list) ? list : []);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to remove admin');
    }
  };

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-pink-400" /></div>;

  return (
    <div className="space-y-6" data-testid="company-management">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-xl font-bold text-gray-900">Companies</h1>
          <p className="text-sm text-gray-500">{companies.length} registered companies</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="bg-pink-500 hover:bg-pink-600 text-white" data-testid="create-company-btn">
              <Plus className="w-4 h-4 mr-2" /> New Company
            </Button>
          </DialogTrigger>
          <DialogContent className="border-gray-200 max-w-lg">
            <DialogHeader><DialogTitle>Register New Company</DialogTitle></DialogHeader>
            <form onSubmit={handleCreate} className="space-y-4">
              {/* Logo Upload */}
              <div className="flex items-center gap-4">
                <div
                  onClick={() => fileInputRef.current?.click()}
                  className="w-16 h-16 rounded-xl border-2 border-dashed border-gray-200 flex items-center justify-center cursor-pointer hover:border-pink-400 hover:bg-pink-50/50 transition-all overflow-hidden"
                  data-testid="logo-upload-area"
                >
                  {logoPreview ? (
                    <img src={logoPreview} alt="Preview" className="w-full h-full object-cover" />
                  ) : (
                    <Upload className="w-5 h-5 text-gray-400" />
                  )}
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-700">Company Logo</p>
                  <p className="text-xs text-gray-400">PNG, JPG, SVG up to 5MB</p>
                </div>
                <input ref={fileInputRef} type="file" accept="image/*" className="hidden" onChange={handleLogoSelect} />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Company Name</Label>
                  <Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value, slug: e.target.value.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '') })} required className="h-9" placeholder="Acme Corp" data-testid="company-name-input" />
                </div>
                <div>
                  <Label className="text-xs">Slug</Label>
                  <Input value={form.slug} onChange={e => setForm({ ...form, slug: e.target.value })} required className="h-9" placeholder="acme-corp" data-testid="company-slug-input" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Email</Label>
                  <Input type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} required className="h-9" placeholder="hr@acme.com" data-testid="company-email-input" />
                </div>
                <div>
                  <Label className="text-xs">Phone</Label>
                  <Input value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} className="h-9" placeholder="+234 801 234 5678" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Registration No.</Label>
                  <Input value={form.registration_number} onChange={e => setForm({ ...form, registration_number: e.target.value })} className="h-9" placeholder="RC-123456" />
                </div>
                <div>
                  <Label className="text-xs">Tax ID</Label>
                  <Input value={form.tax_id} onChange={e => setForm({ ...form, tax_id: e.target.value })} className="h-9" placeholder="TIN-12345" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">City</Label>
                  <Input value={form.city} onChange={e => setForm({ ...form, city: e.target.value })} className="h-9" placeholder="Lagos" />
                </div>
                <div>
                  <Label className="text-xs">State</Label>
                  <Input value={form.state} onChange={e => setForm({ ...form, state: e.target.value })} className="h-9" placeholder="Lagos" />
                </div>
              </div>
              <Button type="submit" disabled={submitting} className="w-full bg-pink-500 hover:bg-pink-600 text-white" data-testid="submit-company-btn">
                {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Create Company'}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Company Grid */}
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {companies.map(c => (
          <Card key={c.id} className="border-gray-200 shadow-sm hover:shadow-md transition-shadow group cursor-pointer" data-testid={`company-card-${c.id}`}
            onClick={() => navigate(`/super-admin/companies/${c.id}`)}>
            <CardContent className="p-4">
              <div className="flex items-start gap-3 mb-3">
                <CompanyLogo logoUrl={c.logo_url} name={c.name} />
                <div className="flex-1 min-w-0">
                  <h3 className="font-semibold text-gray-900 text-sm truncate">{c.name}</h3>
                  <p className="text-xs text-gray-400 truncate">{c.slug}</p>
                </div>
                <span className={`text-[10px] font-medium px-2 py-0.5 rounded-full ${c.status === 'active' ? 'bg-emerald-50 text-emerald-600' : 'bg-gray-100 text-gray-500'}`}>
                  {c.status}
                </span>
              </div>

              <div className="space-y-1.5 text-xs text-gray-500 mb-3">
                {c.email && <div className="flex items-center gap-1.5"><Mail className="w-3 h-3" /> {c.email}</div>}
                {(c.city || c.state) && <div className="flex items-center gap-1.5"><MapPin className="w-3 h-3" /> {[c.city, c.state].filter(Boolean).join(', ')}</div>}
                {c.registration_number && <div className="flex items-center gap-1.5"><Globe className="w-3 h-3" /> {c.registration_number}</div>}
              </div>

              <div className="flex items-center justify-between pt-2 border-t border-gray-100">
                <div className="flex items-center gap-1.5">
                  <span data-testid={`va-status-${c.id}`}
                    className={`text-[10px] font-medium px-2 py-0.5 rounded-full ${c.virtual_account_number ? 'bg-emerald-50 text-emerald-600' : 'bg-orange-50 text-orange-500'}`}>
                    {c.virtual_account_number ? 'Account Active' : 'Not Created'}
                  </span>
                  {c.is_live === false && (
                    <span data-testid={`paused-badge-${c.id}`}
                      className="text-[10px] font-medium px-2 py-0.5 rounded-full bg-red-50 text-red-600">
                      Paused
                    </span>
                  )}
                </div>
                <ChevronRight className="w-4 h-4 text-gray-300 group-hover:text-pink-400 transition-colors" />
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Wallet Info Dialog */}
      <Dialog open={!!selectedCompany} onOpenChange={() => { setSelectedCompany(null); setWalletInfo(null); }}>
        <DialogContent className="border-gray-200">
          <DialogHeader>
            <DialogTitle>{selectedCompany?.name} - Wallet</DialogTitle>
          </DialogHeader>
          {walletLoading ? (
            <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-pink-400" /></div>
          ) : walletInfo ? (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <Card className="border-gray-200">
                  <CardContent className="p-3 text-center">
                    <p className="text-xs text-gray-500 mb-1">Balance</p>
                    <p className="text-lg font-bold text-gray-900" data-testid="wallet-balance">
                      {Number(walletInfo.wallet?.balance || 0).toLocaleString('en-NG', { style: 'currency', currency: 'NGN' })}
                    </p>
                  </CardContent>
                </Card>
                <Card className="border-gray-200">
                  <CardContent className="p-3 text-center">
                    <p className="text-xs text-gray-500 mb-1">Monthly Payroll</p>
                    <p className="text-lg font-bold text-gray-900" data-testid="monthly-payroll">
                      {Number(walletInfo.monthly_payroll || 0).toLocaleString('en-NG', { style: 'currency', currency: 'NGN' })}
                    </p>
                  </CardContent>
                </Card>
              </div>
              <div className="flex items-center gap-2 text-sm text-gray-600">
                <Users className="w-4 h-4" />
                <span>{walletInfo.employees} active employees</span>
              </div>
            </div>
          ) : (
            <p className="text-sm text-gray-500 text-center py-4">No wallet data available</p>
          )}
        </DialogContent>
      </Dialog>

      {/* Company Admin Management Dialog */}
      <Dialog open={!!adminCompany} onOpenChange={() => { setAdminCompany(null); setAdmins([]); }}>
        <DialogContent className="border-gray-200 max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Shield className="w-5 h-5 text-pink-500" />
              {adminCompany?.name} - Admins
            </DialogTitle>
          </DialogHeader>

          {adminsLoading ? (
            <div className="flex justify-center py-6"><Loader2 className="w-6 h-6 animate-spin text-pink-400" /></div>
          ) : (
            <div className="space-y-4">
              {/* Existing admins */}
              {admins.length > 0 && (
                <div className="space-y-2">
                  <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">Current Admins</p>
                  {admins.map(a => (
                    <div key={a.id} className="flex items-center gap-3 p-2.5 rounded-lg bg-gray-50 border border-gray-100" data-testid={`admin-row-${a.id}`}>
                      <div className="w-8 h-8 rounded-full bg-pink-100 flex items-center justify-center shrink-0">
                        <span className="text-xs font-bold text-pink-600">{a.first_name?.[0]}{a.last_name?.[0]}</span>
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900 truncate">{a.first_name} {a.last_name}</p>
                        <p className="text-xs text-gray-500 truncate">{a.email}</p>
                      </div>
                      <span className="text-[10px] font-medium px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-600">{a.role}</span>
                      <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-gray-400 hover:text-red-500 hover:bg-red-50" onClick={() => handleDeleteAdmin(a)} data-testid={`delete-admin-${a.id}`}>
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}

              {/* Create new admin form */}
              <div className="pt-3 border-t border-gray-100">
                <p className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-3">Create New Admin</p>
                <form onSubmit={handleCreateAdmin} className="space-y-3">
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <Label className="text-xs">First Name</Label>
                      <Input value={adminForm.first_name} onChange={e => setAdminForm({...adminForm, first_name: e.target.value})} required className="h-9" placeholder="John" data-testid="admin-first-name" />
                    </div>
                    <div>
                      <Label className="text-xs">Last Name</Label>
                      <Input value={adminForm.last_name} onChange={e => setAdminForm({...adminForm, last_name: e.target.value})} required className="h-9" placeholder="Doe" data-testid="admin-last-name" />
                    </div>
                  </div>
                  <div>
                    <Label className="text-xs">Email</Label>
                    <Input type="email" value={adminForm.email} onChange={e => setAdminForm({...adminForm, email: e.target.value})} required className="h-9" placeholder="admin@company.com" data-testid="admin-email" />
                  </div>
                  <div>
                    <Label className="text-xs">Password</Label>
                    <Input type="password" value={adminForm.password} onChange={e => setAdminForm({...adminForm, password: e.target.value})} required className="h-9" placeholder="Secure password" data-testid="admin-password" />
                  </div>
                  <div>
                    <Label className="text-xs">Phone (optional)</Label>
                    <Input value={adminForm.phone} onChange={e => setAdminForm({...adminForm, phone: e.target.value})} className="h-9" placeholder="+234 801 234 5678" data-testid="admin-phone" />
                  </div>
                  <Button type="submit" disabled={adminSubmitting} className="w-full bg-pink-500 hover:bg-pink-600 text-white" data-testid="create-admin-submit">
                    {adminSubmitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <><UserPlus className="w-4 h-4 mr-2" /> Create Admin</>}
                  </Button>
                </form>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
